import { useState } from "react";
import {
  Drawer,
  Button,
  Table,
  message,
  Spin,
  Alert,
  Space,
  Tag,
  Popconfirm,
  Form,
  Input,
  Select,
  Switch,
  Typography,
} from "antd";
import {
  ReloadOutlined,
  CheckCircleOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  CloseOutlined,
  PrinterOutlined,
  WifiOutlined,
} from "@ant-design/icons";
import {
  drawerTitle,
  drawerFooter,
  DRAWER_WIDE_WIDTH,
} from "../../../../components";
import {
  usePrintersQuery,
  useCreatePrinterMutation,
  useUpdatePrinterMutation,
  useDeletePrinterMutation,
} from "../../../../Services";
import { useActiveStoresQuery } from "../../../../Services";

const { Text } = Typography;

interface Printer {
  id: string;
  store_id: string;
  store_name: string;
  name: string;
  type: string;
  ip_address: string;
  port: number;
  is_default: number;
}

interface Props {
  open: boolean;
  tenantId?: string;
  onClose: () => void;
}

export const PrinterManageDrawer: React.FC<Props> = ({
  open,
  tenantId,
  onClose,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [editingPrinter, setEditingPrinter] = useState<Printer | null>(null);
  const [form] = Form.useForm();

  const {
    data: printers = [],
    isLoading: printersLoading,
    isError,
    isFetching,
    error,
    refetch: refetchPrinters,
  } = usePrintersQuery(tenantId, { enabled: open });

  const { data: stores = [] } = useActiveStoresQuery(tenantId, {
    enabled: open,
  });

  const createMutation = useCreatePrinterMutation();
  const updateMutation = useUpdatePrinterMutation();
  const deleteMutation = useDeletePrinterMutation();

  const mutationPending =
    createMutation.isPending ||
    updateMutation.isPending ||
    deleteMutation.isPending;
  const loading = isFetching || mutationPending;

  const handleCreate = () => {
    setEditingPrinter(null);
    setIsEditing(false);
    setIsCreating(true);
    form.resetFields();
    form.setFieldsValue({ type: "thermal", port: 9100, is_default: false });
  };

  const handleEdit = (printer: Printer) => {
    setIsCreating(false);
    setIsEditing(true);
    setEditingPrinter(printer);
    form.setFieldsValue({
      store_id: printer.store_id,
      name: printer.name,
      type: printer.type,
      ip_address: printer.ip_address,
      port: printer.port,
      is_default: !!printer.is_default,
    });
  };

  const handleCancelForm = () => {
    setIsCreating(false);
    setIsEditing(false);
    setEditingPrinter(null);
    form.resetFields();
  };

  const handleSave = async (values: any) => {
    try {
      if (isCreating) {
        const res = await createMutation.mutateAsync({
          store_id: values.store_id,
          name: values.name,
          type: values.type,
          ip_address: values.ip_address || "",
          port: values.port || 9100,
          is_default: values.is_default ? 1 : 0,
          ...(tenantId ? { tenant_id: tenantId } : {}),
        });
        if (res?.success) {
          message.success(res.message || "Printer created successfully.");
          handleCancelForm();
        } else {
          throw new Error(res?.message || "Failed to create printer.");
        }
      } else if (isEditing && editingPrinter) {
        const res = await updateMutation.mutateAsync({
          id: editingPrinter.id,
          store_id: values.store_id,
          name: values.name,
          type: values.type,
          ip_address: values.ip_address || "",
          port: values.port || 9100,
          is_default: values.is_default ? 1 : 0,
          ...(tenantId ? { tenant_id: tenantId } : {}),
        });
        if (res?.success) {
          message.success(res.message || "Printer updated successfully.");
          handleCancelForm();
        } else {
          throw new Error(res?.message || "Failed to update printer.");
        }
      }
    } catch (err: any) {
      message.error(err.message || "Error saving printer.");
    }
  };

  const handleDelete = async (printer: Printer) => {
    try {
      const res = await deleteMutation.mutateAsync({
        id: Number(printer.id),
        tenant_id: tenantId,
      });
      if (res?.success) {
        message.success(res.message || "Printer deleted successfully.");
      } else {
        throw new Error(res?.message || "Failed to delete printer.");
      }
    } catch (err: any) {
      message.error(err.message || "Error deleting printer.");
    }
  };

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      render: (text: string, printer: Printer) => (
        <Space>
          <Text className="font-semibold text-xs">{text}</Text>
          {!!printer.is_default && (
            <Tag color="blue" className="text-[10px] leading-none">
              DEFAULT
            </Tag>
          )}
        </Space>
      ),
    },
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
      width: 90,
      render: (type: string) => <Tag className="text-[10px]">{type}</Tag>,
    },
    {
      title: "Store",
      dataIndex: "store_name",
      key: "store_name",
      width: 120,
      ellipsis: true,
      render: (text: string) => (
        <span className="text-xs text-gray-600">{text || "-"}</span>
      ),
    },
    {
      title: "IP / Port",
      key: "address",
      width: 130,
      render: (_: any, printer: Printer) => (
        <Space size={4}>
          <WifiOutlined className="text-gray-400 text-[10px]" />
          <span className="text-xs font-mono text-gray-600">
            {printer.ip_address || "N/A"}:{printer.port}
          </span>
        </Space>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      width: 100,
      render: (_: any, printer: Printer) => (
        <Space size="small">
          <Button
            type="text"
            icon={<EditOutlined className="text-blue-500" />}
            size="small"
            onClick={() => handleEdit(printer)}
          />
          <Popconfirm
            title="Are you sure you want to delete this printer?"
            onConfirm={() => handleDelete(printer)}
            okText="Yes"
            cancelText="No"
            placement="left"
          >
            <Button type="text" danger icon={<DeleteOutlined />} size="small" />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const errorMsg = isError
    ? (error as any)?.message || "Failed to load printers."
    : null;

  return (
    <Drawer
      open={open}
      onClose={onClose}
      width={DRAWER_WIDE_WIDTH}
      title={drawerTitle(
        "Manage Printers",
        "Manage printers and their assignments to stores",
      )}
      destroyOnClose
      footer={drawerFooter({ actions: [{ label: "Close", onClick: onClose }] })}
    >
      <div className="flex flex-col gap-4 h-full">
        {!errorMsg && !printersLoading && (
          <Alert
            message={
              <div className="flex items-center justify-between">
                <Space>
                  <CheckCircleOutlined className="text-green-500" />
                  <span className="text-xs font-semibold text-green-800">
                    Connected &mdash; {printers.length} printer(s)
                  </span>
                </Space>
                <Button
                  size="small"
                  icon={<ReloadOutlined />}
                  onClick={() => refetchPrinters()}
                  loading={isFetching && !isCreating && !isEditing}
                  className="text-[11px] h-6"
                >
                  Refresh
                </Button>
              </div>
            }
            type="success"
            showIcon={false}
            className="!border-green-100 !bg-green-50"
          />
        )}

        {errorMsg && (
          <Alert
            message="Connection Error"
            description={
              <div className="flex flex-col gap-2">
                <p className="text-xs text-red-800">{errorMsg}</p>
                <Button
                  size="small"
                  type="primary"
                  danger
                  icon={<ReloadOutlined />}
                  onClick={() => refetchPrinters()}
                  className="w-fit text-xs"
                >
                  Retry Connection
                </Button>
              </div>
            }
            type="error"
            showIcon
          />
        )}

        {!errorMsg && !isCreating && !isEditing && (
          <div className="flex justify-between items-center bg-gray-50 p-2.5 rounded-lg border">
            <Text className="text-xs text-gray-500">
              Manage printers in the POS system.
            </Text>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleCreate}
              size="small"
              className="!bg-black !border-black hover:!bg-gray-800 text-xs"
            >
              Add Printer
            </Button>
          </div>
        )}

        {(isCreating || isEditing) && (
          <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 shadow-sm">
            <div className="flex justify-between items-center mb-3">
              <span className="text-xs font-bold text-slate-800 flex items-center gap-1.5">
                <PrinterOutlined className="text-slate-500" />
                {isCreating ? "New Printer" : "Edit Printer"}
              </span>
              <Button
                type="text"
                size="small"
                shape="circle"
                icon={<CloseOutlined className="text-xs" />}
                onClick={handleCancelForm}
              />
            </div>

            <Form
              form={form}
              layout="vertical"
              onFinish={handleSave}
              size="middle"
            >
              <Form.Item
                name="store_id"
                label="Store"
                rules={[{ required: true, message: "Store is required." }]}
              >
                <Select
                  placeholder="Select store"
                  showSearch
                  filterOption={(input, option) =>
                    (option?.label ?? "")
                      ?.toString()
                      ?.toLowerCase()
                      ?.includes(input?.toLowerCase())
                  }
                  options={stores.map((s: any) => ({
                    value: s.id,
                    label: s.name,
                  }))}
                />
              </Form.Item>

              <Form.Item
                name="name"
                label="Printer Name"
                rules={[
                  { required: true, message: "Printer name is required." },
                ]}
              >
                <Input placeholder="e.g. Kitchen Printer" className="text-xs" />
              </Form.Item>

              <Form.Item
                name="type"
                label="Printer Type"
                rules={[{ required: true, message: "Type is required." }]}
              >
                <Select
                  options={[
                    { value: "thermal", label: "Thermal" },
                    { value: "laser", label: "Laser" },
                    { value: "dot_matrix", label: "Dot Matrix" },
                  ]}
                />
              </Form.Item>

              <Space className="w-full" size="small">
                <Form.Item
                  name="ip_address"
                  label="IP Address"
                  className="flex-1"
                >
                  <Input placeholder="e.g. 192.168.1.100" className="text-xs" />
                </Form.Item>
                <Form.Item
                  name="port"
                  label="Port"
                  className="w-24"
                  rules={[{ pattern: /^\d+$/, message: "Must be a number" }]}
                >
                  <Input placeholder="9100" className="text-xs" />
                </Form.Item>
              </Space>

              <Form.Item
                name="is_default"
                label="Default Printer"
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>

              <Form.Item className="mb-0">
                <Space className="w-full justify-end">
                  <Button size="small" onClick={handleCancelForm}>
                    Cancel
                  </Button>
                  <Button
                    type="primary"
                    htmlType="submit"
                    loading={
                      createMutation.isPending || updateMutation.isPending
                    }
                    size="small"
                    className="!bg-black !border-black hover:!bg-gray-800"
                  >
                    Save Changes
                  </Button>
                </Space>
              </Form.Item>
            </Form>
          </div>
        )}

        <div className="flex-grow overflow-auto relative min-h-[200px]">
          {printersLoading && printers.length === 0 && (
            <div className="flex items-center justify-center h-40">
              <Spin tip="Loading printers..." size="large" />
            </div>
          )}

          {!errorMsg && printers.length > 0 && (
            <Table
              dataSource={printers}
              columns={columns}
              rowKey="id"
              size="small"
              pagination={false}
              className="border rounded-lg"
              locale={{ emptyText: "No printers found." }}
            />
          )}

          {!errorMsg &&
            printers.length === 0 &&
            !printersLoading &&
            !isCreating &&
            !isEditing && (
              <div className="text-center text-gray-400 text-sm py-10">
                No printers found. Click "Add Printer" to create one.
              </div>
            )}
        </div>
      </div>
    </Drawer>
  );
};
